import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { User, ArrowRight, Mail, MessageCircle } from "lucide-react";
import { claimIdentity } from "@/lib/tracker";

export default function AuthModal({ open, onClose }) {
  const { requestOTP, verifyOTP, login } = useAuth();
  const [step, setStep] = useState("method");
  const [authMethod, setAuthMethod] = useState("phone");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const countdownRef = useRef(null);

  // Start 30-second countdown
  const startResendTimer = () => {
    setResendCountdown(30);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []);

  const handleRequestOTP = async () => {
    try {
      setError("");
      setLoading(true);
      if (authMethod === "phone") {
        const res = await requestOTP(phone);
        setProvider(res.provider || "whatsapp");
      } else {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/email/request`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
          signal: controller.signal,
        });
        clearTimeout(timeout);
        let data;
        try { data = await res.json(); } catch { throw new Error("Server error. Please try again."); }
        if (!res.ok) throw new Error(data.detail || "Failed to send OTP");
        setProvider("email");
      }
      setStep("otp");
      startResendTimer();
      setError("");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "";
      const msgLower = msg.toLowerCase();
      if (msg === "Load failed" || msg === "Failed to fetch" || e.name === 'AbortError') {
        setError("Network error. Please check your connection and try again.");
      } else if (msgLower.includes("wait") || msgLower.includes("cooldown") || msgLower.includes("seconds")) {
        // Rate limit — OTP was already sent, just move to OTP step
        setStep("otp");
        startResendTimer();
      } else {
        setError(msg || "Failed to send OTP");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setError("");
      setLoading(true);
      if (authMethod === "phone") {
        const res = await verifyOTP(phone, otp);
        if (res?.needs_phone === false) {
          // Phone login — phone is inherently available
        }
      } else {
        let lastErr = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 15000);
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/email/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email: email.trim().toLowerCase(), otp: otp.trim() }),
              signal: controller.signal,
            });
            clearTimeout(timeout);
            let data;
            try { data = await res.json(); } catch { throw new Error("Server returned an invalid response. Please try again."); }
            if (!res.ok) throw new Error(data.detail || "Invalid OTP");
            login(data.token, data.role || "CUSTOMER", data.user);
            if (data.user?.id) claimIdentity(data.user.id, null, email, 'email');
            // Store needs_phone flag for the compulsory phone popup
            if (data.needs_phone) {
              localStorage.setItem("nc_needs_phone", "true");
            }
            lastErr = null;
            break;
          } catch (fetchErr) {
            lastErr = fetchErr;
            if (fetchErr.name === 'AbortError') {
              lastErr = new Error("Request timed out. Please check your connection and try again.");
              break;
            }
            if (fetchErr.message?.includes("Invalid OTP") || fetchErr.message?.includes("No OTP")) break;
            if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
          }
        }
        if (lastErr) throw lastErr;
      }
      onClose();
      resetForm();
    } catch (e) {
      const msg = e.message || "";
      if (msg === "Load failed" || msg === "Failed to fetch" || msg === "NetworkError when attempting to fetch resource.") {
        setError("Network error. Please check your internet connection and try again.");
      } else {
        setError(e.response?.data?.detail || msg || "Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCountdown > 0) return;
    try {
      setError("");
      setLoading(true);
      if (authMethod === "phone") {
        await requestOTP(phone);
      } else {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/email/request`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || "Failed to resend OTP");
        }
      }
      startResendTimer();
    } catch (e) {
      // Suppress rate-limit / cooldown errors — OTP is already sent and valid
      const msg = (e.response?.data?.detail || e.message || "").toLowerCase();
      if (msg.includes("wait") || msg.includes("cooldown") || msg.includes("seconds")) {
        // Silently restart timer, don't show error
        startResendTimer();
      } else {
        setError(e.response?.data?.detail || e.message || "Failed to resend OTP");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => { setStep("method"); setPhone(""); setEmail(""); setOtp(""); setError(""); setProvider(""); setResendCountdown(0); if (countdownRef.current) clearInterval(countdownRef.current); };
  const selectMethod = (method) => { setAuthMethod(method); setStep(method); setError(""); };

  const otpSentVia = authMethod === "phone" ? "WhatsApp" : "Email";

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); resetForm(); }}>
      <DialogContent className="bg-white border-0 max-w-md p-0 overflow-hidden rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="bg-[#FFB800] px-6 py-6 sm:py-8 text-[#1A1A1A] text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-black/10 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
            {step === "otp"
              ? <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8 text-[#1A1A1A]" />
              : <User className="w-7 h-7 sm:w-8 sm:h-8 text-[#1A1A1A]" />}
          </div>
          <DialogTitle className="text-xl sm:text-2xl font-black tracking-tight mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
            {step === "method" ? "Sign In" : step === "otp" ? "Enter OTP" : "Sign In"}
          </DialogTitle>
          <p className="text-[#1A1A1A]/70 text-xs sm:text-sm font-medium">
            {step === "method" ? "Welcome back to NirvanaComfort"
              : step === "otp" ? `Check your ${otpSentVia} for the code`
              : authMethod === "phone" ? "Enter your WhatsApp number" : "Enter your email address"}
          </p>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="p-5 sm:p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="auth-error">{error}</div>}

          {step === "method" && (
            <div className="space-y-3">
              <button type="button"
                onClick={() => {
                  // Preserve the page the user is on (e.g. /checkout, /cart)
                  // so the Google OAuth callback can bounce them back after
                  // sign-in. sessionStorage survives the full-page redirect;
                  // the callback clears it immediately after use.
                  try {
                    const rt = window.location.pathname + (window.location.search || "");
                    if (rt && rt !== "/") sessionStorage.setItem("nc_auth_return_to", rt);
                  } catch { /* private-mode / storage disabled */ }
                  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
                  const redirect = window.location.origin + "/auth/google/callback";
                  const scope = "openid email profile";
                  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirect)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
                }}
                data-testid="auth-method-google"
                className="w-full flex items-center justify-center gap-3 h-12 rounded-full border border-gray-300 hover:border-gray-400 hover:shadow-md bg-white transition-all cursor-pointer active:scale-[0.98]">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                <span className="text-sm font-medium text-gray-700" style={{fontFamily:'-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif', letterSpacing:'0.2px'}}>Continue with Google</span>
              </button>
              <div className="text-center"><p className="text-xs font-medium text-gray-400">or use OTP</p></div>
              <button onClick={() => selectMethod("phone")} data-testid="auth-method-whatsapp"
                className="w-full flex items-center justify-center gap-3 h-12 rounded-full border border-gray-300 hover:border-gray-400 hover:shadow-md bg-white transition-all cursor-pointer active:scale-[0.98]">
                <svg className="w-5 h-5 flex-shrink-0 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span className="text-sm font-medium text-gray-700" style={{fontFamily:'-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif', letterSpacing:'0.2px'}}>Login via WhatsApp</span>
              </button>
              <button onClick={() => selectMethod("email")} data-testid="auth-method-email"
                className="w-full flex items-center justify-center gap-3 h-12 rounded-full border border-gray-300 hover:border-gray-400 hover:shadow-md bg-white transition-all cursor-pointer active:scale-[0.98]">
                <Mail className="w-5 h-5 flex-shrink-0 text-indigo-600" />
                <span className="text-sm font-medium text-gray-700" style={{fontFamily:'-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif', letterSpacing:'0.2px'}}>Continue with Email</span>
              </button>
              <p className="text-center text-[11px] text-gray-400 pt-1">By continuing, you agree to our Terms & Privacy Policy</p>
            </div>
          )}

          {step === "phone" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">WhatsApp Number</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">+91</span>
                  <Input data-testid="auth-phone-input" placeholder="" value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleRequestOTP(); } }}
                    className="pl-14 h-12 rounded-xl border-2 border-gray-200 focus:border-green-500 text-base sm:text-lg" />
                </div>
                <p className="text-xs text-gray-400">We'll send a 6-digit code to this WhatsApp number</p>
              </div>
              <Button data-testid="auth-request-otp-btn" className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-semibold"
                onClick={handleRequestOTP} disabled={loading || !phone.trim()}>
                {loading ? "Sending..." : "Send OTP on WhatsApp"}
              </Button>
              <button className="w-full text-sm text-gray-500 hover:text-green-600 transition-colors py-2" onClick={() => setStep("method")}>
                Back
              </button>
            </div>
          )}

          {step === "email" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Email Address</label>
                <Input data-testid="auth-email-input" type="email" placeholder="" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleRequestOTP(); } }}
                  className="h-12 rounded-xl border-2 border-gray-200 focus:border-indigo-500 text-base sm:text-lg" />
              </div>
              <Button data-testid="auth-request-email-otp-btn" className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-semibold"
                onClick={handleRequestOTP} disabled={loading || !email.trim()}>
                {loading ? "Sending..." : "Send OTP"}
              </Button>
              <button className="w-full text-sm text-gray-500 hover:text-indigo-600 transition-colors py-2" onClick={() => setStep("method")}>
                Back
              </button>
            </div>
          )}

          {step === "otp" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Verification Code</label>
                <Input data-testid="auth-otp-input" type="text" inputMode="numeric" autoComplete="one-time-code"
                  placeholder="" value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} maxLength={6}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleVerifyOTP(); } }}
                  className="h-14 rounded-xl border-2 border-gray-200 focus:border-green-500 text-2xl text-center tracking-[0.5em] font-mono" />
                <p className="text-xs text-center text-gray-500">
                  {authMethod === "phone"
                    ? "Check your WhatsApp for the verification code"
                    : "Check your email for the verification code"}
                </p>
              </div>
              <Button data-testid="auth-verify-otp-btn" className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-semibold"
                onClick={handleVerifyOTP} disabled={loading || otp.length < 6}>
                {loading ? "Verifying..." : "Verify & Sign In"}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button className="text-gray-500 hover:text-green-600 transition-colors py-2" onClick={() => setStep(authMethod)}>
                  Change {authMethod === "phone" ? "number" : "email"}
                </button>
                <button
                  className={`font-medium transition-colors py-2 ${resendCountdown > 0 ? "text-gray-400 cursor-not-allowed" : "text-green-600 hover:text-green-700"}`}
                  onClick={handleResendOTP}
                  disabled={loading || resendCountdown > 0}
                  data-testid="resend-otp-btn"
                >
                  {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend OTP"}
                </button>
              </div>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
